import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import CustomRichTextEditor from '../../components/CustomRichTextEditor';
import './PatientList.css';

export default function MessageTemplates() {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: '', body: '' });

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const res = await axios.get(`${API_URL}message-templates`, {
        headers: { 'Accept': 'application/json' },
      });

      const data = res?.data?.data || [];
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate('/control/login');
        return;
      }
      setError(e?.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    if (API_URL) fetchTemplates();
  }, [API_URL, fetchTemplates]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', type: '', body: '' });
    setShowModal(true);
    setError('');
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      name: tpl?.name || '',
      type: tpl?.type || '',
      body: tpl?.body || '',
    });
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!form.name.trim() || !form.type.trim()) {
      setError('Please enter name and type');
      return;
    }

    if (!form.body || !String(form.body).trim()) {
      setError('Please enter template content');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        name: form.name,
        type: form.type,
        body: form.body,
      };

      if (editing?.id) {
        await axios.put(`${API_URL}message-templates/${editing.id}`, payload, {
          headers: { 'Accept': 'application/json' },
        });
      } else {
        await axios.post(`${API_URL}message-templates`, payload, {
          headers: { 'Accept': 'application/json' },
        });
      }

      closeModal();
      await fetchTemplates();
    } catch (e2) {
      if (e2?.response?.status === 401) {
        navigate('/control/login');
        return;
      }
      if (e2?.response?.data?.errors) {
        const messages = Object.values(e2.response.data.errors).flat().join(' ');
        setError(messages || 'Failed to save template');
        return;
      }
      setError(e2?.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!tpl?.id) return;
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      setSaving(true);
      setError('');

      await axios.delete(`${API_URL}message-templates/${tpl.id}`, {
        headers: { 'Accept': 'application/json' },
      });

      await fetchTemplates();
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate('/control/login');
        return;
      }
      setError(e?.response?.data?.message || 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mt-4" style={{ direction: 'ltr', textAlign: 'left' }}>
      <div className="patient-list-card">
        <div className="patient-list-header d-flex justify-content-between align-items-center">
          <h2 className="patient-list-title">
            <i className="fas fa-file-alt me-2"></i>
            Message Templates
          </h2>
          <button className="btn create-patient-btn" onClick={openCreate} disabled={saving}>
            <i className="fas fa-plus me-2"></i>
            Add Template
          </button>
        </div>

        <div className="p-3">
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="alert alert-info" role="alert">
              No templates yet.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Type</th>
                    <th scope="col" style={{ width: '200px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((tpl) => (
                    <tr key={tpl.id}>
                      <td>{tpl.name}</td>
                      <td>{tpl.type}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(tpl)} disabled={saving}>
                            <i className="fas fa-edit me-1"></i>
                            Edit
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(tpl)} disabled={saving}>
                            <i className="fas fa-trash me-1"></i>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Template' : 'Add Template'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Example: Post-visit instructions"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={form.type}
                onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                required
              >
                <option value="" disabled>
                  Select type...
                </option>
                <option value="Post-surgery">Post-surgery</option>
                <option value="General">General</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <CustomRichTextEditor
                value={form.body || ''}
                onChange={(html) => setForm((p) => ({ ...p, body: html }))}
                placeholder="Write template content here..."
                style={{ minHeight: '220px' }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
